// "Meu dia" (formato do Habblaud): janela sobre o escritório com o tempo dos agentes no dia.
// Cartões (esperando você, trabalhando, sessões, pedidos, tokens, custo), horas, projetos, maiores
// esperas e contas. Lê GET /api/day a cada 30 s enquanto estiver aberta; fecha no ✕ e no Esc.
//
// Abre e fecha por window 'agzos:painel' com detail 'dia' (o botão da barra manda esse evento).
// Tempo de agentes é a soma de todos eles (dois agentes por 1 h = 2 h). "Esperando você" é a mão
// levantada: permissão, pergunta ou escolha. Só conta enquanto o escritório está rodando.

import type { DayAccountSummary, DayProject, DaySummary, DayWait, Minutos } from '../core/daylog.js';
import type { Agent, OfficeSnapshot } from '../core/types.js';
import { localeTag, onLangChange, plural, t } from './i18n.js';
import { custoTexto, tokensTexto } from './stats.js';
import { PROVIDER_COLORS, PROVIDER_LABELS, PROVIDER_TAGS } from './sprites.js';

/** O que GET /api/day devolve (src/http/day.ts). `demo` quando os números são fictícios. */
type Resposta = ({ ok: true; dias: string[]; demo?: true } & DaySummary) | { ok: false; error: string };

/** As quatro cores do painel: cada status cai numa delas. */
type Categoria = 'trabalhando' | 'esperando' | 'shell' | 'ocioso';
const CATEGORIAS: readonly Categoria[] = ['trabalhando', 'esperando', 'shell', 'ocioso'];
/** Status que a legenda escondeu: somem dos gráficos (as tabelas mostram tudo). */
const ocultas = new Set<Categoria>();

const ATUALIZA_MS = 30_000;
const DIA_ID = 'dia';

let aberto = false;
/** Dia escolhido (AAAA-MM-DD); undefined = hoje. */
let dia: string | undefined;
/** Último dia lido: a resposta, ou 'erro'. undefined enquanto carrega. */
let dados: (DaySummary & { dias: string[]; demo?: true }) | 'erro' | undefined;
let dias: string[] = [];
let atualizadoEm = 0;
let pedido = 0;
let relogio: number | undefined;
let raiz: HTMLElement | undefined;
let focoAntes: HTMLElement | null = null;
let visaoHoras: 'grafico' | 'tabela' = 'grafico';
let visaoProjetos: 'grafico' | 'tabela' = 'grafico';
let agoraDoEscritorio: () => OfficeSnapshot | null = () => null;

/**
 * Monta o painel: registra o botão na barra, ouve 'agzos:painel' e o Esc. Chamado uma vez, pelo main.
 * `agora` devolve o snapshot atual (para o número de agentes esperando no botão).
 */
export function montaDia(opcoes: { agora: () => OfficeSnapshot | null }): void {
  agoraDoEscritorio = opcoes.agora;
  // o botão é da barra (src/web/toolbar.ts): ele pede o painel pelo evento 'agzos:painel'
  window.addEventListener('agzos:painel', (evento) => {
    if ((evento as CustomEvent).detail === DIA_ID) alternaDia();
  });
  // capture: o Esc fecha só esta janela (o atalho de Esc do escritório não corre)
  document.addEventListener(
    'keydown',
    (evento) => {
      if (!aberto || evento.key !== 'Escape') return;
      evento.preventDefault();
      evento.stopPropagation();
      fechaDia();
    },
    true,
  );
  onLangChange(() => {
    if (!aberto) return;
    raiz?.remove();
    raiz = undefined;
    abreCasca();
    pinta();
  });
}

function alternaDia(): void {
  if (aberto) fechaDia();
  else abreDia();
}

function abreDia(): void {
  if (aberto) return;
  aberto = true;
  focoAntes = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  marcaBotao();
  abreCasca();
  carrega();
  relogio = window.setInterval(carrega, ATUALIZA_MS);
  raiz?.querySelector<HTMLButtonElement>('.mdia-fechar')?.focus();
}

function fechaDia(): void {
  if (!aberto) return;
  aberto = false;
  if (relogio !== undefined) window.clearInterval(relogio);
  relogio = undefined;
  raiz?.remove();
  raiz = undefined;
  marcaBotao();
  focoAntes?.focus?.();
}

/** Liga ou desliga um status nos gráficos. Não apaga o último ligado (sem ele o gráfico ficaria vazio). */
function alternaCategoria(c: Categoria): void {
  if (ocultas.has(c)) ocultas.delete(c);
  else if (ocultas.size < CATEGORIAS.length - 1) ocultas.add(c);
  pinta();
}

/** O botão do topo mostra se a janela está aberta. */
function marcaBotao(): void {
  document.querySelector('#btn-dia')?.setAttribute('aria-pressed', String(aberto));
}

/** A casca da janela (cabeçalho, seletor de dia, corpo). O corpo é refeito a cada leitura. */
function abreCasca(): void {
  const el = document.createElement('section');
  el.className = 'mdia';
  el.innerHTML = `<div class="mdia-caixa" role="dialog" aria-modal="true" aria-labelledby="mdia-titulo">
    <header class="mdia-cab">
      <h2 id="mdia-titulo">${t('topo.meu_dia')}</h2>
      <label class="mdia-escolha"><span class="mdia-rotulo-campo">${t('mdia.dia')}</span><select class="mdia-dia" aria-label="${t('mdia.dia_aria')}"></select></label>
      <button class="mdia-fechar" type="button" aria-label="${t('acao.fechar')}" title="${t('acao.fechar')}">✕</button>
    </header>
    <p class="mdia-atualizado" aria-live="polite"></p>
    <div class="mdia-corpo"></div>
  </div>`;
  el.addEventListener('click', (evento) => {
    const alvo = (evento.target as HTMLElement).closest<HTMLElement>('button, [data-acao]');
    if (!alvo) return;
    if (alvo.matches('.mdia-fechar')) return fechaDia();
    const acao = alvo.dataset.acao;
    if (acao === 'legenda') {
      alternaCategoria(alvo.dataset.cat as Categoria);
    } else if (acao === 'horas') {
      visaoHoras = visaoHoras === 'grafico' ? 'tabela' : 'grafico';
      pinta();
    } else if (acao === 'projetos') {
      visaoProjetos = visaoProjetos === 'grafico' ? 'tabela' : 'grafico';
      pinta();
    } else if (acao === 'timelapse') {
      // o timelapse (B21) abre sobre o campus: esta janela sai do caminho
      const data = dia ?? chaveDoDia(new Date());
      fechaDia();
      window.dispatchEvent(new CustomEvent('agzos:painel', { detail: { tipo: 'timelapse', data } }));
    }
  });
  el.querySelector<HTMLSelectElement>('.mdia-dia')?.addEventListener('change', (evento) => {
    const valor = (evento.target as HTMLSelectElement).value;
    dia = valor === chaveDoDia(new Date()) ? undefined : valor;
    dados = undefined;
    pinta();
    carrega();
  });
  // clicar no fundo não fecha: só o ✕ e o Esc (como pedido)
  document.body.append(el);
  raiz = el;
}

/** Lê o dia escolhido. Respostas velhas (o dia já mudou) são descartadas. */
function carrega(): void {
  const alvo = dia ?? chaveDoDia(new Date());
  const seq = ++pedido;
  fetch(`/api/day?date=${encodeURIComponent(alvo)}`)
    .then((r) => r.json() as Promise<Resposta>)
    .then((json) => {
      if (seq !== pedido) return;
      if (json.ok) {
        dados = json;
        dias = json.dias;
      } else {
        dados = 'erro';
      }
      atualizadoEm = Date.now();
    })
    .catch(() => {
      if (seq === pedido) dados = 'erro';
    })
    .finally(() => {
      if (seq === pedido) pinta();
    });
}

/** Redesenha o corpo e o seletor a partir do que já foi lido. */
function pinta(): void {
  if (!raiz) return;
  const corpo = raiz.querySelector<HTMLElement>('.mdia-corpo');
  if (corpo) {
    // o foco fica no mesmo botão de ação depois de redesenhar (a leitura de 30 s não o derruba)
    const foco = document.activeElement instanceof HTMLElement && corpo.contains(document.activeElement) ? document.activeElement : undefined;
    const seletor = foco?.dataset.acao ? `[data-acao="${foco.dataset.acao}"]${foco.dataset.cat ? `[data-cat="${foco.dataset.cat}"]` : ''}` : '';
    corpo.innerHTML = corpoHtml();
    if (seletor) corpo.querySelector<HTMLElement>(seletor)?.focus();
  }
  const atualizado = raiz.querySelector<HTMLElement>('.mdia-atualizado');
  if (atualizado) atualizado.textContent = t('mdia.atualizado', { hora: atualizadoEm ? hhmmss(atualizadoEm) : '—' });
  pintaSeletor();
}

/** Relê o dia que está aberto (quem liga ou desliga a demonstração chama, para os números trocarem já). */
export function atualizaDia(): void {
  if (aberto) carrega();
}

/** As opções do seletor: Hoje, Ontem e os dias guardados. Só refaz se a lista mudou (não derruba o menu aberto). */
function pintaSeletor(): void {
  const seletor = raiz?.querySelector<HTMLSelectElement>('.mdia-dia');
  if (!seletor) return;
  const hoje = chaveDoDia(new Date());
  const ontem = diaVizinho(hoje, -1);
  const escolhido = dia ?? hoje;
  const valores = [...new Set([hoje, ontem, ...dias, escolhido])].filter((d) => d <= hoje).sort().reverse();
  const assinatura = valores.join('|');
  if (seletor.dataset.lista === assinatura) return;
  seletor.dataset.lista = assinatura;
  seletor.innerHTML = valores
    .map((d) => {
      const rotulo = d === hoje ? t('mdia.hoje') : d === ontem ? t('mdia.ontem') : rotuloDia(d);
      return `<option value="${d}"${d === escolhido ? ' selected' : ''}>${escape(rotulo)}</option>`;
    })
    .join('');
}

function corpoHtml(): string {
  if (dados === undefined) return `<p class="mdia-aviso" role="status">${t('dia.carregando')}</p>`;
  if (dados === 'erro') return `<p class="mdia-aviso" role="status">${t('dia.erro')}</p>`;
  // o selo "demonstração" fica no topo, no aviso: no cabeçalho ele empurrava o ✕ para outra linha no celular
  const aviso = dados.demo ? `<p class="mdia-aviso-demo" role="note"><span class="selo-demo">${t('demo.selo')}</span> ${t('mdia.demo_aviso')}</p>` : '';
  return [aviso, cartoes(dados), legenda(), horasSecao(dados), projetosSecao(dados), esperasSecao(dados), contasSecao(dados), rodape()].join('');
}

// ------------------------------------------------------------------ cartões

function cartoes(r: DaySummary & { dias: string[] }): string {
  const { minutes, pedidos, ferramentas, concluidas, tokens } = r.totals;
  const trabalho = minutes.working + minutes.thinking;
  const maior = r.esperas.top[0];
  const tempoMaior = maior ? duracao(duracaoMin(maior)) : '';
  return `<div class="mdia-cartoes">
    <article class="mdia-cartao mdia-espera-cartao">
      <h3>${t('mdia.esperando')}</h3>
      <p class="mdia-grande">${duracao(minutes.waiting)}</p>
      <p class="mdia-sub">${plural('mdia.esperas', r.esperas.total)}</p>
      ${maior ? `<p class="mdia-sub">${t('mdia.maior', { duracao: tempoMaior, nome: escape(maior.name), projeto: escape(maior.project || '—'), hora: hhmm(maior.inicio) })}</p>` : ''}
    </article>
    <article class="mdia-cartao">
      <h3>${t('mdia.trabalhando')}</h3>
      <p class="mdia-grande">${duracao(trabalho)}</p>
      <p class="mdia-sub">${t('mdia.trabalhando_sub')}</p>
    </article>
    <article class="mdia-cartao">
      <h3>${t('mdia.sessoes')}</h3>
      <p class="mdia-grande">${r.sessoes}</p>
      <p class="mdia-sub">${plural('mdia.subagentes', r.subagentes)}</p>
    </article>
    <article class="mdia-cartao">
      <h3>${t('mdia.pedidos')}</h3>
      <p class="mdia-grande">${pedidos} <small>${plural('mdia.pedido', pedidos)}</small></p>
      <p class="mdia-sub">${ferramentas} ${plural('mdia.ferramenta', ferramentas)} · ${concluidas} ${plural('mdia.concluida', concluidas)}</p>
    </article>
    <article class="mdia-cartao">
      <h3>${t('mdia.tokens')}</h3>
      <p class="mdia-grande">${tokens ? tokens.toLocaleString(localeTag()) : '—'}</p>
      <p class="mdia-sub">${r.totals.tokensIn || r.totals.tokensOut ? t('stats.entrada_saida', { e: tokensTexto(r.totals.tokensIn), s: tokensTexto(r.totals.tokensOut) }) : t('mdia.tokens_sub')}</p>
    </article>
    <article class="mdia-cartao">
      <h3>${t('mdia.custo')}</h3>
      <p class="mdia-grande">${custoTexto(r.totals.custo)}</p>
      <p class="mdia-sub">${r.totals.custo === null ? t('mdia.custo_sem') : t('stats.custo_sub')}</p>
    </article>
  </div>`;
}

function legenda(): string {
  const itens: Array<[Categoria, string]> = [
    ['esperando', t('mdia.leg.esperando')],
    ['trabalhando', t('mdia.leg.trabalhando')],
    ['ocioso', t('mdia.leg.ocioso')],
    ['shell', t('mdia.leg.shell')],
  ];
  return `<ul class="mdia-legenda" aria-label="${escape(t('mdia.legenda'))}">${itens
    .map(
      ([c, rotulo]) =>
        `<li><button type="button" class="mdia-leg${ocultas.has(c) ? ' oculto' : ''}" data-acao="legenda" data-cat="${c}" aria-pressed="${!ocultas.has(c)}" title="${escape(t('mdia.leg_alterna'))}"><span class="mdia-ponto mdia-${c}"></span>${rotulo}</button></li>`,
    )
    .join('')}</ul>`;
}

// ------------------------------------------------------------------ por hora

function horasSecao(r: DaySummary): string {
  const hoje = dia === undefined || dia === chaveDoDia(new Date());
  const agora = new Date().getHours();
  const totais = r.horas.map(somaVisivel);
  const max = Math.max(1, ...totais);
  const botao = `<button type="button" class="mdia-alterna" data-acao="horas" aria-pressed="${visaoHoras === 'tabela'}">${visaoHoras === 'tabela' ? t('mdia.ver_grafico') : t('mdia.ver_tabela')}</button>`;
  const corpo =
    visaoHoras === 'tabela'
      ? tabelaHoras(r.horas)
      : `<div class="mdia-horas" role="img" aria-label="${escape(t('mdia.horas_aria'))}">
          ${r.horas
            .map((h, i) => {
              const total = totais[i] ?? 0;
              const atual = hoje && i === agora;
              const dica = `${i}h · ${CATEGORIAS.map((c) => `${rotuloCategoria(c)}: ${duracao(categorias(h)[c])}`).join(' · ')}`;
              return `<div class="mdia-hora${atual ? ' atual' : ''}" title="${escape(dica)}">
                <div class="mdia-pilha" style="height:${(total / max) * 100}%">${segmentos(h)}</div>
                <span class="mdia-eixo">${i % 3 === 0 ? `${i}h` : ''}</span>
              </div>`;
            })
            .join('')}
        </div>`;
  return `<section class="mdia-secao" aria-labelledby="mdia-h-horas">
    <div class="mdia-secao-cab"><h3 id="mdia-h-horas">${t('mdia.por_hora')}</h3>${botao}</div>
    ${temHoras(r) ? corpo : `<p class="mdia-vazio">${t('dia.vazio')}</p>`}
  </section>`;
}

function tabelaHoras(horas: Minutos[]): string {
  const linhas = horas
    .map((h, i) => {
      const c = categorias(h);
      return `<tr><th scope="row">${i}h</th>${CATEGORIAS.map((k) => `<td>${c[k] ? duracao(c[k]) : '—'}</td>`).join('')}</tr>`;
    })
    .join('');
  return `<div class="mdia-tabela-caixa"><table class="mdia-tabela">
    <caption>${t('mdia.tabela_horas')}</caption>
    <thead><tr><th scope="col">${t('mdia.col_hora')}</th>${CATEGORIAS.map((k) => `<th scope="col">${rotuloCategoria(k)}</th>`).join('')}</tr></thead>
    <tbody>${linhas}</tbody>
  </table></div>`;
}

// ------------------------------------------------------------------ por projeto

function projetosSecao(r: DaySummary): string {
  // ordenado pelo tempo esperando você; empate: quem mais trabalhou
  const projetos = [...r.projects].sort((a, b) => b.minutes.waiting - a.minutes.waiting || trabalhoDe(b) - trabalhoDe(a) || a.project.localeCompare(b.project));
  const max = Math.max(1, ...projetos.map((p) => somaVisivel(p.minutes)));
  const botao = `<button type="button" class="mdia-alterna" data-acao="projetos" aria-pressed="${visaoProjetos === 'tabela'}">${visaoProjetos === 'tabela' ? t('mdia.ver_grafico') : t('mdia.ver_tabela')}</button>`;
  let corpo: string;
  if (!projetos.length) corpo = `<p class="mdia-vazio">${t('dia.vazio')}</p>`;
  else if (visaoProjetos === 'tabela') corpo = tabelaProjetos(projetos);
  else
    corpo = `<ul class="mdia-projetos">${projetos
      .map((p) => {
        const total = somaBucket(p);
        return `<li class="mdia-projeto">
          <div class="mdia-projeto-topo"><b>${escape(p.project || '—')}</b><span>${t('mdia.proj_linha', { espera: duracao(p.minutes.waiting), total: duracao(total) })}</span></div>
          <div class="mdia-barra" style="width:${(somaVisivel(p.minutes) / max) * 100}%" role="img" aria-label="${escape(`${p.project || '—'}: ${duracao(total)}`)}">${segmentos(p.minutes)}</div>
        </li>`;
      })
      .join('')}</ul>`;
  return `<section class="mdia-secao" aria-labelledby="mdia-h-projetos">
    <div class="mdia-secao-cab"><h3 id="mdia-h-projetos">${t('mdia.por_projeto')}</h3>${botao}</div>
    ${corpo}
  </section>`;
}

function tabelaProjetos(projetos: DayProject[]): string {
  const linhas = projetos
    .map((p) => {
      const c = categorias(p.minutes);
      return `<tr><th scope="row">${escape(p.project || '—')}</th>${CATEGORIAS.map((k) => `<td>${c[k] ? duracao(c[k]) : '—'}</td>`).join('')}<td>${duracao(somaBucket(p))}</td></tr>`;
    })
    .join('');
  return `<div class="mdia-tabela-caixa"><table class="mdia-tabela">
    <caption>${t('mdia.tabela_projetos')}</caption>
    <thead><tr><th scope="col">${t('mdia.col_projeto')}</th>${CATEGORIAS.map((k) => `<th scope="col">${rotuloCategoria(k)}</th>`).join('')}<th scope="col">${t('mdia.col_total')}</th></tr></thead>
    <tbody>${linhas}</tbody>
  </table></div>`;
}

// ------------------------------------------------------------------ maiores esperas

function esperasSecao(r: DaySummary): string {
  const top = r.esperas.top;
  const max = Math.max(1, ...top.map(duracaoMin));
  const corpo = top.length
    ? `<ol class="mdia-esperas">${top
        .map((e, i) => {
          const conta = contaDaEspera(e, r.contas);
          const fim = e.aberta ? t('mdia.agora') : hhmm(e.fim);
          return `<li class="mdia-espera">
            <span class="mdia-pos">${i + 1}</span>
            <div class="mdia-espera-corpo">
              <div class="mdia-espera-topo">
                <b>${duracao(duracaoMin(e))}</b>
                <span class="mdia-selo" style="background:${escape(conta.cor)}">${escape(conta.sigla)}</span>
                <span class="mdia-nome">${escape(e.name)}</span>
                <small>${escape(e.project || '—')}</small>
              </div>
              <div class="mdia-barra-fina"><i style="width:${(duracaoMin(e) / max) * 100}%"></i></div>
              <small class="mdia-espera-meta">${hhmm(e.inicio)}–${fim} · ${motivoLabel(e.motivo)}</small>
            </div>
          </li>`;
        })
        .join('')}</ol>`
    : `<p class="mdia-vazio">${t('mdia.nenhuma_espera')}</p>`;
  return `<section class="mdia-secao" aria-labelledby="mdia-h-esperas"><h3 id="mdia-h-esperas">${t('mdia.maiores_esperas')}</h3>${corpo}</section>`;
}

// ------------------------------------------------------------------ por conta

function contasSecao(r: DaySummary): string {
  const trabalho = r.contas.reduce((soma, c) => soma + trabalhoDe(c), 0);
  const corpo = r.contas.length
    ? `<div class="mdia-contas">${r.contas.map((c) => contaCartao(c, trabalho)).join('')}</div>`
    : `<p class="mdia-vazio">${t('dia.vazio')}</p>`;
  return `<section class="mdia-secao" aria-labelledby="mdia-h-contas"><h3 id="mdia-h-contas">${t('mdia.por_conta')}</h3>${corpo}</section>`;
}

function contaCartao(c: DayAccountSummary, trabalhoTotal: number): string {
  const fatia = trabalhoTotal ? (trabalhoDe(c) / trabalhoTotal) * 100 : 0;
  const nome = c.label || PROVIDER_LABELS[c.provider] || c.id;
  const sigla = c.tag || PROVIDER_TAGS[c.provider] || '?';
  const cor = c.color || PROVIDER_COLORS[c.provider] || '#888';
  return `<article class="mdia-conta">
    <header><span class="mdia-selo" style="background:${escape(cor)}">${escape(sigla)}</span><b>${escape(nome)}</b></header>
    <p class="mdia-sub">${c.sessoes} ${plural('mdia.sessao', c.sessoes)} · ${c.subagentes} ${t('mdia.sub')}</p>
    <div class="mdia-fatia" role="img" aria-label="${escape(t('mdia.conta_fatia', { p: Math.round(fatia) }))}"><i style="width:${fatia}%;background:${escape(cor)}"></i></div>
    <p class="mdia-sub">${t('mdia.conta_fatia', { p: Math.round(fatia) })}</p>
    <dl class="mdia-dl">
      <div><dt>${t('mdia.trabalhando')}</dt><dd>${duracao(trabalhoDe(c))}</dd></div>
      <div><dt>${t('mdia.esperando')}</dt><dd>${duracao(c.minutes.waiting)}</dd></div>
      <div><dt>${t('mdia.tokens')}</dt><dd>${c.tokens ? c.tokens.toLocaleString(localeTag()) : '—'}</dd></div>
      <div><dt>${t('mdia.custo')}</dt><dd>${custoTexto(c.custo)}</dd></div>
    </dl>
  </article>`;
}

// ------------------------------------------------------------------ rodapé

function rodape(): string {
  return `<footer class="mdia-rodape">
    <p>${t('mdia.rodape_1')}</p>
    <p>${t('mdia.rodape_2')}</p>
    <p>${t('mdia.rodape_3')}</p>
    <button type="button" class="mdia-timelapse" data-acao="timelapse">${t('mdia.timelapse')}</button>
  </footer>`;
}

// ------------------------------------------------------------------ peças

/** As quatro categorias a partir dos status (trabalhando = trabalhando + pensando). */
function categorias(m: Minutos): Record<Categoria, number> {
  return { trabalhando: m.working + m.thinking, esperando: m.waiting, shell: m.shell, ocioso: m.idle };
}

/** As categorias que o gráfico mostra: as que a legenda deixou ligadas. */
function categoriasVisiveis(m: Minutos): Record<Categoria, number> {
  const c = categorias(m);
  for (const k of CATEGORIAS) if (ocultas.has(k)) c[k] = 0;
  return c;
}

function somaVisivel(m: Minutos): number {
  const c = categoriasVisiveis(m);
  return CATEGORIAS.reduce((soma, k) => soma + c[k], 0);
}

function segmentos(m: Minutos): string {
  const c = categoriasVisiveis(m);
  return CATEGORIAS.filter((k) => c[k] > 0)
    .map((k) => `<i class="mdia-seg mdia-${k}" style="flex-grow:${c[k]}" aria-hidden="true"></i>`)
    .join('');
}

function rotuloCategoria(c: Categoria): string {
  switch (c) {
    case 'trabalhando':
      return t('mdia.leg.trabalhando');
    case 'esperando':
      return t('mdia.leg.esperando');
    case 'shell':
      return t('mdia.leg.shell');
    default:
      return t('mdia.leg.ocioso');
  }
}

function motivoLabel(motivo: DayWait['motivo']): string {
  switch (motivo) {
    case 'permissao':
      return t('mdia.motivo.permissao');
    case 'escolha':
      return t('mdia.motivo.escolha');
    default:
      return t('mdia.motivo.pergunta');
  }
}

function somaMinutos(m: Minutos): number {
  return m.working + m.thinking + m.waiting + m.shell + m.idle;
}

function somaBucket(b: { minutes: Minutos }): number {
  return somaMinutos(b.minutes);
}

/** Tempo de trabalho (trabalhando + pensando) de um bucket. */
function trabalhoDe(b: { minutes: Minutos }): number {
  return b.minutes.working + b.minutes.thinking;
}

function temHoras(r: DaySummary): boolean {
  return r.horas.some((h) => somaMinutos(h) > 0);
}

function esperandoAgora(): number {
  return agoraDoEscritorio()?.agents.filter((a: Agent) => a.status === 'waiting').length ?? 0;
}

function duracaoMin(e: DayWait): number {
  return Math.max(0, e.fim - e.inicio) / 60_000;
}

/** A conta de uma espera: sigla, cor e a cor do provider quando a conta não está no dia. */
function contaDaEspera(e: DayWait, contas: DaySummary['contas']): { sigla: string; cor: string } {
  const chave = e.accountId ?? e.provider;
  const conta = contas.find((c) => c.id === chave);
  return {
    sigla: conta?.tag || PROVIDER_TAGS[e.provider] || '?',
    cor: conta?.color || PROVIDER_COLORS[e.provider] || '#888',
  };
}

/** Minutos como '45 min' ou '2 h 5 min'. */
function duracao(minutos: number): string {
  const m = Math.round(minutos);
  return m < 60 ? t('tempo.min', { n: m }) : t('tempo.h_min', { h: Math.floor(m / 60), m: m % 60 });
}

const p2 = (n: number): string => String(n).padStart(2, '0');

/** HH:MM no fuso local (para as esperas e o "às"). */
function hhmm(ms: number): string {
  const d = new Date(ms);
  return `${p2(d.getHours())}:${p2(d.getMinutes())}`;
}

function hhmmss(ms: number): string {
  const d = new Date(ms);
  return `${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`;
}

/** Data local AAAA-MM-DD (a mesma conta de dayKey no servidor). */
function chaveDoDia(d: Date): string {
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
}

/** O dia anterior ou o seguinte de uma data AAAA-MM-DD (meio-dia evita o salto do horário de verão). */
function diaVizinho(data: string, passo: number): string {
  const d = new Date(`${data}T12:00:00`);
  d.setDate(d.getDate() + passo);
  return chaveDoDia(d);
}

function rotuloDia(data: string): string {
  return new Date(`${data}T12:00:00`).toLocaleDateString(localeTag(), { weekday: 'long', day: 'numeric', month: 'long' });
}

const escape = (valor: string): string => valor.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);
